import React, { useRef, useEffect, useState } from "react";

// Components

// Reducers
import { myStore } from "App";
import { useSelector } from "react-redux";
import { setRects } from "~/reducers";
import '~/styles/Stage.css';

// Icons
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStop } from '@fortawesome/free-solid-svg-icons';

// Functions
import { cancelExecution, restaurarAvatarsAoEstadoOriginal } from '~/functions/nodeFunctions';
import { setExecuting } from '~/actions/nodes';

// Styles
import "~/styles/Stage.css";

export const InteractiveStage = () => {
	const rects = useSelector((state) => state.rects.rects);
	const avatars = useSelector((state) => state.avatars);
	const nodes = useSelector((state) => state.nodes.nodes || []);
	const edges = useSelector((state) => state.edges.edges || []);
    // Pega o estado de execução do Redux
    const isExecuting = useSelector(state => state.nodes.isExecuting || false);
	const stageRef = useRef(null);
	const containerRef = useRef(null);
	const [zoomLevel, setZoomLevel] = useState(1);
	const [theaterMode, setTheaterMode] = useState(false);
	const [theaterModeEnabled, setTheaterModeEnabled] = useState(true); // Por padrão ativo
	const [showStoppedMessage, setShowStoppedMessage] = useState(false);
	const [stageBackground, setStageBackground] = useState({ url: null, fit: 'cover' });
	const wasTheaterRef = useRef(false);

	const findPrimaryBackgroundFromFlow = (allNodes, allEdges) => {
		try {
			if (!Array.isArray(allNodes) || allNodes.length === 0) return null;
			const nodesMap = allNodes.reduce((map, n) => {
				if (n?.id) map[n.id] = n;
				return map;
			}, {});

			const startNode = allNodes.find(n => n?.id === '1' || n?.type === 'Inicio' || n?.data?.label === 'Inicio') || allNodes[0];
			let currentId = startNode?.id;
			const visited = new Set();

			while (currentId && !visited.has(currentId)) {
				visited.add(currentId);
				const current = nodesMap[currentId];
				if (current && (current.type === 'Fundo' || current.data?.label === 'Fundo')) {
					return {
						url: current.data?.backgroundUrl || null,
						fit: current.data?.backgroundFit || 'cover'
					};
				}

				let nextEdge = null;
				if (current?.type === 'Decidir' || current?.data?.label === 'Decidir') {
					nextEdge = allEdges.find(e => e.source === currentId && e.sourceHandle === 'verdadeiro')
						|| allEdges.find(e => e.source === currentId && e.sourceHandle === 'falso')
						|| allEdges.find(e => e.source === currentId);
				} else {
					nextEdge = allEdges.find(e => e.source === currentId);
				}
				currentId = nextEdge ? nextEdge.target : null;
			}

			const anyFundo = allNodes.find(n => n?.type === 'Fundo' || n?.data?.label === 'Fundo');
			if (!anyFundo) return null;
			return {
				url: anyFundo.data?.backgroundUrl || null,
				fit: anyFundo.data?.backgroundFit || 'cover'
			};
		} catch (e) {
			console.warn('⚠️ Erro ao derivar fundo do fluxo:', e);
			return null;
		}
	};

	// Listener: execução pode alterar fundo via evento
	useEffect(() => {
		const handle = (event) => {
			const url = event?.detail?.url ?? null;
			const fit = event?.detail?.fit || 'cover';
			setStageBackground({ url, fit });
		};
		window.addEventListener('stageBackgroundChanged', handle);
		return () => window.removeEventListener('stageBackgroundChanged', handle);
	}, []);

	// Preview fora de execução: deriva do primeiro nó Fundo no fluxo
	useEffect(() => {
		if (isExecuting) return;
		const derived = findPrimaryBackgroundFromFlow(nodes, edges);
		setStageBackground(prev => {
			const nextUrl = derived?.url ?? null;
			const nextFit = derived?.fit || 'cover';
			if (prev.url === nextUrl && prev.fit === nextFit) return prev;
			return { url: nextUrl, fit: nextFit };
		});
	}, [nodes, edges, isExecuting]);
	const zoomIn = () => {
		const container = containerRef.current;
		const stage = stageRef.current;
		const oldZoom = zoomLevel;
		const newZoom = Math.min(oldZoom + 0.1, 3);
		const cw = container.offsetWidth, ch = container.offsetHeight;
		const cx = container.scrollLeft + cw / 2, cy = container.scrollTop + ch / 2;
		const scale = newZoom / oldZoom;
		const ncx = cx * scale, ncy = cy * scale;
		const maxLeft = stage.offsetWidth * newZoom - cw;
		const maxTop = stage.offsetHeight * newZoom - ch;
		container.scrollLeft = Math.max(0, Math.min(ncx - cw / 2, maxLeft));
		container.scrollTop = Math.max(0, Math.min(ncy - ch / 2, maxTop));
		setZoomLevel(newZoom);
	};
	const zoomOut = () => {
		const container = containerRef.current;
		const stage = stageRef.current;
		const oldZoom = zoomLevel;
		const newZoom = Math.max(oldZoom - 0.1, 0.5);
		const cw = container.offsetWidth, ch = container.offsetHeight;
		const cx = container.scrollLeft + cw / 2, cy = container.scrollTop + ch / 2;
		const scale = newZoom / oldZoom;
		const ncx = cx * scale, ncy = cy * scale;
		const maxLeft = stage.offsetWidth * newZoom - cw;
		const maxTop = stage.offsetHeight * newZoom - ch;
		container.scrollLeft = Math.max(0, Math.min(ncx - cw / 2, maxLeft));
		container.scrollTop = Math.max(0, Math.min(ncy - ch / 2, maxTop));
		setZoomLevel(newZoom);
	};
	const isPanning = useRef(false);
	const pan = useRef({ startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });

	// Carregar configuração do modo teatro e escutar mudanças
	useEffect(() => {
		// Carregar configuração salva
		const savedTheaterMode = localStorage.getItem('theaterModeEnabled');
		console.log('🎭 Carregando configuração do localStorage:', savedTheaterMode);
		
		if (savedTheaterMode !== null) {
			const enabled = JSON.parse(savedTheaterMode);
			setTheaterModeEnabled(enabled);
			console.log('🎭 Modo teatro configurado:', enabled);
		} else {
			console.log('🎭 Usando configuração padrão: true');
		}

		// Escutar mudanças na configuração
		const handleConfigChange = (event) => {
			console.log('🎭 Configuração alterada:', event.detail.enabled);
			setTheaterModeEnabled(event.detail.enabled);
		};

		window.addEventListener('theaterModeConfigChanged', handleConfigChange);
		return () => window.removeEventListener('theaterModeConfigChanged', handleConfigChange);
	}, []);

	// Efeito para ativar/desativar modo teatro baseado na execução
	useEffect(() => {
		// Só ativa o modo teatro se estiver habilitado nas configurações
		if (isExecuting && !theaterMode && theaterModeEnabled) {
			// Ativa modo teatro com delay para suavizar a transição
			setTimeout(() => {
				setTheaterMode(true);
				// Centraliza automaticamente quando entra no modo teatro
				setTimeout(() => {
					recenter();
				}, 100);
			}, 300);
		} else if (!isExecuting && theaterMode) {
			// Desativa modo teatro; recentralização será tratada no listener de transição
			setTheaterMode(false);
		}
	}, [isExecuting, theaterMode, theaterModeEnabled]);

	// Garante recentralização suave após sair do modo teatro, quando a transição de layout terminar
	useEffect(() => {
		const container = containerRef.current;
		// executa apenas na transição de true -> false
		if (wasTheaterRef.current && !theaterMode && container) {
			let handled = false;
			const onEnd = (e) => {
				if (handled) return;
				// Considera mudanças de tamanho/posicionamento
				if (!e || e.target === container) {
					handled = true;
					// aguarda um frame para layout estabilizar
					requestAnimationFrame(() => requestAnimationFrame(() => recenter()));
				}
			};
			container.addEventListener('transitionend', onEnd, { once: true });
			// fallback caso o evento não dispare (ex.: browser/cancelamento)
			const t = setTimeout(() => onEnd(), 1000);
			return () => {
				container.removeEventListener('transitionend', onEnd);
				clearTimeout(t);
			};
		}

		// atualiza o estado anterior
		wasTheaterRef.current = theaterMode;
	}, [theaterMode]);

	// Teclas de atalho para modo teatro
	useEffect(() => {
		const handleKeyPress = (e) => {
			if (e.key === 'Escape') {
				if (theaterMode && isExecuting) {
					// ESC para parar execução durante execução
					e.preventDefault();
					handleStopExecution();
				}
			}
			
			// ENTER para confirmar ações (botões OK ou inputs do nó Ler)
			if (e.key === 'Enter') {
				e.preventDefault();
				
				// Procura por botão de confirmação visível (Mostrar)
				const confirmButton = document.querySelector('.stage-ok-button');
				if (confirmButton) {
					console.log('🎯 ENTER pressionado - clicando no botão OK');
					confirmButton.click();
					return;
				}
				
				// Procura por input do nó Ler ativo
				const activeInput = document.querySelector('.stage-element input[type="text"]:focus');
				if (activeInput) {
					console.log('🎯 ENTER pressionado - disparando evento inputLer');
					const rectElement = activeInput.closest('.stage-element');
					if (rectElement) {
						const rectId = rectElement.id;
						window.dispatchEvent(new CustomEvent('inputLer', { 
							detail: { rectId: rectId, value: activeInput.value } 
						}));
						return;
					}
				}
				
				// Se não há input focado, mas há um input visível do nó Ler, dispara o evento
				const visibleInput = document.querySelector('.stage-element input[type="text"]');
				if (visibleInput) {
					console.log('🎯 ENTER pressionado - disparando evento inputLer no input visível');
					const rectElement = visibleInput.closest('.stage-element');
					if (rectElement) {
						const rectId = rectElement.id;
						window.dispatchEvent(new CustomEvent('inputLer', { 
							detail: { rectId: rectId, value: visibleInput.value } 
						}));
						return;
					}
				}
			}
		};

		document.addEventListener('keydown', handleKeyPress);
		return () => document.removeEventListener('keydown', handleKeyPress);
	}, [theaterMode, isExecuting]);

	// center view on mount
	useEffect(() => {
		const container = containerRef.current;
		const stageEl = stageRef.current;
		container.scrollLeft = stageEl.offsetWidth / 2 - container.offsetWidth / 2;
		container.scrollTop = stageEl.offsetHeight / 2 - container.offsetHeight / 2;
		container.style.cursor = 'grab';
	}, []);

	const handleMouseDown = (e) => {
		if (isExecuting) return; // bloqueia pan enquanto executando
		// Bloqueia pan se houver texto selecionado
		if (window.getSelection && window.getSelection().toString()) return;
		isPanning.current = true;
		pan.current.startX = e.clientX;
		pan.current.startY = e.clientY;
		pan.current.scrollLeft = containerRef.current.scrollLeft;
		pan.current.scrollTop = containerRef.current.scrollTop;
		containerRef.current.style.cursor = 'grabbing';
	};
	const handleMouseMove = (e) => {
		if (!isPanning.current) return;
		const dx = e.clientX - pan.current.startX;
		const dy = e.clientY - pan.current.startY;
		const container = containerRef.current;
		const stage = stageRef.current;
		// desired scroll positions
		let newLeft = pan.current.scrollLeft - dx;
		let newTop = pan.current.scrollTop - dy;
		// calculate max scroll based on zoom
		const maxLeft = stage.offsetWidth * zoomLevel - container.offsetWidth;
		const maxTop = stage.offsetHeight * zoomLevel - container.offsetHeight;
		// clamp within [0, max]
		container.scrollLeft = Math.max(0, Math.min(newLeft, maxLeft));
		container.scrollTop = Math.max(0, Math.min(newTop, maxTop));
	};
	const handleMouseUp = () => { 
		isPanning.current = false; 
		containerRef.current.style.cursor = 'grab';
	};

	const recenter = () => {
		if (!containerRef.current || !stageRef.current) {
			console.warn('⚠️ Refs não disponíveis para recentralizar');
			return;
		}
		
		const container = containerRef.current;
		const stage = stageRef.current;
		console.log('🎯 Recentralizando palco...', { zoomLevel });
		
		// center taking zoom into account
		const centerX = stage.offsetWidth * zoomLevel / 2 - container.offsetWidth / 2;
		const centerY = stage.offsetHeight * zoomLevel / 2 - container.offsetHeight / 2;
		container.scrollLeft = Math.max(0, centerX);
		container.scrollTop = Math.max(0, centerY);
		
		console.log('🎯 Palco recentralizado');
	};

	// update position after drag end
	const handleDragEnd = (e) => {
		const id = e.target.id;
		const rect = stageRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left - rect.width / 2;
		const y = -(e.clientY - rect.top - rect.height / 2); // Invertendo o Y
		const newRects = rects.map((r) => (r.id === id ? { ...r, x, y } : r));
		myStore.dispatch(setRects(newRects));
	};

	// Handler para parar execução no modo teatro
	const handleStopExecution = () => {
		cancelExecution();
		restaurarAvatarsAoEstadoOriginal();
		myStore.dispatch(setExecuting(false));
		myStore.dispatch(setRects([]));
		
		// Mostra mensagem de parada
		setShowStoppedMessage(true);
		setTimeout(() => setShowStoppedMessage(false), 2000);
		
		// Sai do modo teatro após parar execução
		setTimeout(() => {
			setTheaterMode(false);
		}, 500); // Pequeno delay para transição suave
	};

	return (
		<div style={{ height: '50%', width: '100%', position: 'relative', overflow: 'hidden' }}>
			{/* Botões de controle - escondidos em modo teatro, posicionados como no rf-test */}
			{!theaterMode && (
				<>
					<button className="zoom-in-button" onClick={zoomIn}><AddIcon/></button>
					<button className="zoom-out-button" onClick={zoomOut}><RemoveIcon/></button>
					<button className="recenter-button" onClick={recenter}><FullscreenIcon/></button>
				</>
			)}
			
			{/* Botões e controles do modo teatro - RENDERIZADOS FORA DO CONTAINER */}
			{theaterMode && (
				<>
					{/* Informações do modo teatro */}
					<div className="theater-info">
						<span>Modo Teatro Ativo</span>
						<small>
							ESC para PARAR
						</small>
					</div>
					
					{/* Mensagem de execução parada */}
					{showStoppedMessage && (
						<div className="theater-stopped-message">
							<span>⏹️ Execução Interrompida</span>
						</div>
					)}
				</>
			)}
			
			{/* Botão para parar execução - só aparece se estiver executando E em modo teatro */}
			{theaterMode && isExecuting && (
				<button 
					className="theater-stop-button"
					onClick={handleStopExecution}
					title="Parar execução (ESC)"
				>
					<FontAwesomeIcon icon={faStop} />
					<span>Parar</span>
				</button>
			)}
			
			{/* Botão para sair do modo teatro - só aparece quando NÃO está executando */}
			{theaterMode && !isExecuting && (
				<button 
					className="exit-theater-button"
					onClick={() => setTheaterMode(false)}
					title="Sair do modo teatro (ESC)"
				>
					✕
				</button>
			)}
			
			{/* Container do palco com classe condicional para modo teatro */}
			<div
				className={`stage-container ${theaterMode ? 'stage-theater-mode' : ''}`}
				ref={containerRef}
				style={{ width: '100%', height: '100%' }}
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUp}
				onMouseLeave={handleMouseUp}
			>
				<div
					ref={stageRef}
					className={`stage ${theaterMode ? 'stage-theater' : ''} ${stageBackground?.url ? 'stage-has-background' : ''}`}
					style={{
						transform: `scale(${zoomLevel})`,
						transformOrigin: '0 0',
						position: 'relative',
						...(stageBackground?.url ? { backgroundColor: 'transparent' } : {}),
						...(stageBackground?.url
							? {
								backgroundImage: `url(${stageBackground.url})`,
								backgroundSize: stageBackground.fit || 'cover',
								backgroundRepeat: 'no-repeat',
								backgroundPosition: 'center'
							}
							: {})
					}}
					onDragOver={(e) => e.preventDefault()}
				>
					{/* axis tick labels every 100px */}
					{!stageBackground?.url && (
						<>
							{Array.from({ length: 21 }, (_, i) => i * 100 - 1000)
								.filter((tick) => tick !== 0)
								.map((tick) => (
									<div
										key={`xlabel-${tick}`}
										className="axis-label x"
										style={{ left: `calc(50% + ${tick}px)`, top: '50%' }}
									>
										{tick / 10}
									</div>
								))}
							{Array.from({ length: 21 }, (_, i) => i * 100 - 1000)
								.filter((tick) => tick !== 0)
								.map((tick) => (
									<div
										key={`ylabel-${tick}`}
										className="axis-label y"
										style={{ top: `calc(50% + ${-tick}px)`, left: '50%' }}
									>
										{tick / 10}
									</div>
								))}
						</>
					)}
					{rects.filter((rect) => rect.visible).map((rect) => {
						// render OK as button
						if (rect.id.startsWith('confirm-')) {
							return (
								<button
									key={rect.id}
									className="stage-ok-button"
									style={{
										position: 'absolute',
										left: `calc(50% + ${rect.x}px)`,
										top: `calc(50% + ${-rect.y}px)`,
										transform: 'translate(-50%, -50%)',
									}}
									onClick={() => window.dispatchEvent(new CustomEvent('clickConfirm', { detail: { rectId: rect.id } }))}
								>
									OK
								</button>
							);
						}
						// render input para retângulo de leitura
						if (rect.id.startsWith('ler-')) {
							// Suporte a ambos: rect.inputType (novo) ou rect.tipo (caso antigo)
							const tipoInput = rect.inputType || rect.tipo;
							console.log('tipoInput', tipoInput)
							return (
								<div
									id={rect.id}
									key={rect.id}
									className="stage-element"
									style={{
										left: `calc(50% + ${rect.x}px)`,
										top: `calc(50% + ${-rect.y}px)`,
										minWidth: 220,
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 12,
									}}
								>
									<div style={{ marginBottom: 10 }}>{rect.text}</div>
										{tipoInput === 'logico' ? (
										<div style={{ display: 'flex', gap: 16 }}>
											<label style={{ cursor: 'pointer' }}>
												<input
													type="radio"
													name={`ler-radio-${rect.id}`}
													value="true"
													onChange={() => window.dispatchEvent(new CustomEvent('inputLer', { detail: { rectId: rect.id, value: true } }))}
												/>
												Sim
											</label>
											<label style={{ cursor: 'pointer' }}>
												<input
													type="radio"
													name={`ler-radio-${rect.id}`}
													value="false"
													onChange={() => window.dispatchEvent(new CustomEvent('inputLer', { detail: { rectId: rect.id, value: false } }))}
												/>
												Não
											</label>
										</div>
									) : (
										<input
											type="text"
											style={{ padding: 6, borderRadius: 6, border: '1px solid #bbb', minWidth: 120 }}
											onChange={e => window.dispatchEvent(new CustomEvent('inputLer', { detail: { rectId: rect.id, value: e.target.value } }))}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault();
													console.log('🎯 ENTER no input - disparando evento inputLer');
													window.dispatchEvent(new CustomEvent('inputLer', { detail: { rectId: rect.id, value: e.target.value } }));
												}
											}}
											placeholder="Digite aqui..."
											autoFocus
										/>
									)}
								</div>
							);
						}
						// render input para retângulo de interação (perguntas)
						if (rect.id.startsWith('interagir-') && rect.balloonType === 'input') {
							const tipoInput = rect.tipo || 'texto';
							return (
								<div
									id={rect.id}
									key={rect.id}
									className="stage-input-balloon"
									style={{
										left: `calc(50% + ${rect.x}px)`,
										top: `calc(50% + ${-rect.y}px)`,
										minWidth: 220,
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 12,
									}}
								>
									<div style={{ fontWeight: 'bold', textAlign: 'center' }}>{rect.text}</div>
									{tipoInput === 'logico' ? (
										<div style={{ display: 'flex', gap: 15 }}>
											<label style={{ cursor: 'pointer' }}>
												<input
													type="radio"
													name={`interagir-radio-${rect.id}`}
													value="true"
													onChange={() => window.dispatchEvent(new CustomEvent('inputLer', { detail: { rectId: rect.id, value: true } }))}
												/>
												Sim
											</label>
											<label style={{ cursor: 'pointer' }}>
												<input
													type="radio"
													name={`interagir-radio-${rect.id}`}
													value="false"
													onChange={() => window.dispatchEvent(new CustomEvent('inputLer', { detail: { rectId: rect.id, value: false } }))}
												/>
												Não
											</label>
										</div>
									) : (
										<input
											type="text"
											style={{ padding: 6, borderRadius: 6, border: '1px solid #ffd54f', minWidth: 120 }}
											onChange={e => window.dispatchEvent(new CustomEvent('inputLer', { detail: { rectId: rect.id, value: e.target.value } }))}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault();
													console.log('🎯 ENTER no input de interação - disparando evento inputLer');
													window.dispatchEvent(new CustomEvent('inputLer', { detail: { rectId: rect.id, value: e.target.value } }));
												}
											}}
											placeholder="Digite sua resposta..."
											autoFocus
										/>
									)}
								</div>
							);
						}
						// render balões de fala
						if (rect.balloonType === 'speech') {
							return (
								<div
									id={rect.id}
									key={rect.id}
									className="stage-speech-balloon"
									data-position={rect.balloonPosition || "acima"}
									style={{
										left: `calc(50% + ${rect.x}px)`,
										top: `calc(50% + ${-rect.y}px)`,
									}}
								>
									{rect.text}
								</div>
							);
						}
						// render balões de pensamento
						if (rect.balloonType === 'thought') {
							return (
								<div
									id={rect.id}
									key={rect.id}
									className="stage-thought-balloon"
									data-position={rect.balloonPosition || "acima"}
									style={{
										left: `calc(50% + ${rect.x}px)`,
										top: `calc(50% + ${-rect.y}px)`,
									}}
								>
									{rect.text}
								</div>
							);
						}
						// normal rect
						return (
							<div
								id={rect.id}
								key={rect.id}
								className="stage-element"
								draggable={!isExecuting}
								onDragEnd={handleDragEnd}
								style={{
									left: `calc(50% + ${rect.x}px)`,
									top: `calc(50% + ${-rect.y}px)`,
								}}
								onClick={() => {
									// no-op for normal rects
								}}
							>
								{rect.text}
							</div>
						);
					})}
					{/* Renderizar avatares visíveis */}
					{avatars.filter((avatar) => avatar.visivel && avatar.nome).map((avatar, index) => (
						<div
							key={`avatar-${index}`}
							className="stage-avatar"
							style={{
								position: 'absolute',
								left: `calc(50% + ${avatar.x}px)`,
								top: `calc(50% + ${-avatar.y}px)`,
								transform: `translate(-50%, -50%) rotate(${avatar.rotacao || 0}deg)`,
								zIndex: 10,
							}}
						>
							{avatar.imagem ? (
								<img 
									src={avatar.imagem} 
									alt={avatar.nome || "Avatar"} 
									style={{ 
										width: `${avatar.tamanho}px`, 
										height: `${avatar.tamanho}px`,
										objectFit: 'cover',
										borderRadius: '8px',
										boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
									}}
								/>
							) : (
								<div 
									style={{
										width: `${avatar.tamanho}px`, 
										height: `${avatar.tamanho}px`,
										background: '#e9ecef',
										border: '2px solid #adb5bd',
										borderRadius: '8px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										color: '#6c757d',
										fontSize: `${Math.min(avatar.tamanho / 3, 24)}px`
									}}
								>
									👤
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};
